import { z } from "zod";
import { Region, RegionCode } from "./region.type";

/** All language codes supported by the platform. */
export enum LanguageCode {
  english = "en",
  french = "fr",
  spanish = "es",
  german = "de",
  italian = "it",
  portugueseBR = "pt-BR",
  portuguesePT = "pt-PT",
  dutch = "nl",
  polish = "pl",
  swedish = "sv",
  welsh = "cy",
  irish = "ga",
  scottishGaelic = "gd",
  hindi = "hi",
  catalan = "ca",
  galician = "gl",
  basque = "eu",
  romansh = "rm",
  maori = "mi",
  tamil = "ta",
  telugu = "te",
  bengali = "bn",
  marathi = "mr",
  japanese = "ja",
}
export enum Language {
  english = "english",
  french = "français",
  spanish = "español",
  german = "deutsch",
  italian = "italiano",
  portugueseBR = "português (Brasil)",
  portuguesePT = "português (Portugal)",
  dutch = "Nederlands",
  polish = "polski",
  swedish = "svenska",
  welsh = "Cymraeg",
  irish = "Gaeilge",
  scottishGaelic = "Gàidhlig",
  hindi = "हिन्दी",
  catalan = "català",
  galician = "galego",
  basque = "euskara",
  romansh = "rumantsch",
  maori = "te reo Māori",
  tamil = "தமிழ்",
  telugu = "తెలుగు",
  bengali = "বাংলা",
  marathi = "मराठी",
  japanese = "日本語",
}

/** All language ids that may appear in URLs / `LocaleDetails.languageCode`. */
export const LanguageTypeSchema = z.enum([
  LanguageCode.english,
  LanguageCode.french,
  LanguageCode.spanish,
  LanguageCode.german,
  LanguageCode.italian,
  LanguageCode.portugueseBR,
  LanguageCode.portuguesePT,
  LanguageCode.dutch,
  LanguageCode.polish,
  LanguageCode.swedish,
  LanguageCode.welsh,
  LanguageCode.irish,
  LanguageCode.scottishGaelic,
  LanguageCode.hindi,
  LanguageCode.catalan,
  LanguageCode.galician,
  LanguageCode.basque,
  LanguageCode.romansh,
  LanguageCode.maori,
  LanguageCode.tamil,
  LanguageCode.telugu,
  LanguageCode.bengali,
  LanguageCode.marathi,
  LanguageCode.japanese,
]);

export type LanguageType = z.infer<typeof LanguageTypeSchema>;

/** Subset with full UI / prompt coverage today (`Record<CoreLanguageType, T>`). */
export const CoreLanguageTypeSchema = z.enum([
  LanguageCode.english,
  LanguageCode.french,
  LanguageCode.spanish,
  LanguageCode.german,
  LanguageCode.italian,
  LanguageCode.portugueseBR,
  LanguageCode.portuguesePT,
  LanguageCode.dutch,
  LanguageCode.polish,
  LanguageCode.swedish,
  LanguageCode.welsh,
  LanguageCode.irish,
  LanguageCode.scottishGaelic,
  LanguageCode.hindi,
  LanguageCode.catalan,
  LanguageCode.galician,
  LanguageCode.basque,
  LanguageCode.romansh,
  LanguageCode.maori,
  LanguageCode.tamil,
  LanguageCode.telugu,
  LanguageCode.bengali,
  LanguageCode.marathi,
  LanguageCode.japanese,
]);

export type CoreLanguageType = z.infer<typeof CoreLanguageTypeSchema>;

/** Same as `LanguageType` — alias for clarity at locale boundaries. */
export type LocaleLanguageId = LanguageType;

/** Map any locale id to a core language for `Record<CoreLanguageType, T>` lookups. */
export function toCoreLanguageType(code: LanguageType): CoreLanguageType {
  const parsed = CoreLanguageTypeSchema.safeParse(code);
  return parsed.success ? parsed.data : LanguageCode.english;
}

/** Lets `record[locale.languageCode]` resolve to English for languages we have not translated yet. */
export function proxyLanguageRecord<T extends object>(
  core: Partial<Record<CoreLanguageType, T>> & { en: T },
): Record<LanguageType, T> {
  return new Proxy(core, {
    get(target, prop: string | symbol) {
      if (typeof prop !== 'string') return undefined;
      const k = toCoreLanguageType(prop as LanguageType);
      return (target as Record<string, T>)[k] ?? (target as Record<string, T>).en;
    },
  }) as Record<LanguageType, T>;
}

export const SitemapObligatoryLanguages = [
  LanguageCode.english,
  LanguageCode.french,
  LanguageCode.spanish,
];

export type Locale =
  | `${LanguageType}-${RegionCode}`
  | "pt-BR"
  | "pt-PT";

export type LocaleDetails = {
  path: Locale;
  region: Region;
  regionCode: RegionCode;
  language: Language;
  languageCode: LanguageType;
  default?: Locale;
};

export function getLanguageDisplayName(locale: LocaleDetails | undefined): string {
  if (!locale) return "";

  switch (locale.languageCode) {
    case LanguageCode.english:
      return "English";
    case LanguageCode.french:
      return "French (Français)";
    case LanguageCode.spanish:
      return "Spanish (Español)";
    case LanguageCode.german:
      return "German (Deutsch)";
    case LanguageCode.italian:
      return "Italian (Italiano)";
    case LanguageCode.portugueseBR:
      return "Portuguese (Brazil)";
    case LanguageCode.portuguesePT:
      return "Portuguese (Portugal)";
    case LanguageCode.dutch:
      return "Dutch (Nederlands)";
    case LanguageCode.polish:
      return "Polish (Polski)";
    case LanguageCode.swedish:
      return "Swedish (Svenska)";
    case LanguageCode.welsh:
      return "Welsh (Cymraeg)";
    case LanguageCode.irish:
      return "Irish (Gaeilge)";
    case LanguageCode.scottishGaelic:
      return "Scottish Gaelic (Gàidhlig)";
    case LanguageCode.catalan:
      return "Catalan (Català)";
    case LanguageCode.galician:
      return "Galician (Galego)";
    case LanguageCode.basque:
      return "Basque (Euskara)";
    case LanguageCode.romansh:
      return "Romansh (Rumantsch)";
    case LanguageCode.maori:
      return "Māori (Te reo Māori)";
    case LanguageCode.hindi:
      return "Hindi (हिन्दी)";
    case LanguageCode.tamil:
      return "Tamil (தமிழ்)";
    case LanguageCode.telugu:
      return "Telugu (తెలుగు)";
    case LanguageCode.bengali:
      return "Bengali (বাংলা)";
    case LanguageCode.marathi:
      return "Marathi (मराठी)";
    case LanguageCode.japanese:
      return "Japanese (日本語)";
    default:
      return locale.language;
  }
}

// 1. Define your countryLanguages with all relevant locales:
// Note: "pt-BR" and "pt-PT" use abbreviated forms rather than the strict LanguageCode-RegionCode pattern
export const countryLanguages: Record<string, LocaleDetails> = {
  "en-CA": {
    path: "en-CA",
    region: Region.Canada,
    regionCode: RegionCode.Canada,
    language: Language.english,
    languageCode: LanguageCode.english,
  },
  "fr-CA": {
    path: "fr-CA",
    region: Region.Canada,
    regionCode: RegionCode.Canada,
    language: Language.french,
    default: "en-CA",
    languageCode: LanguageCode.french,
  },
  "en-US": {
    path: "en-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.english,
    languageCode: LanguageCode.english,
  },
  "es-US": {
    path: "es-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.spanish,
    default: "en-US",
    languageCode: LanguageCode.spanish,
  },
  "fr-US": {
    path: "fr-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.french,
    default: "en-US",
    languageCode: LanguageCode.french,
  },
  "en-FR": {
    path: "en-FR",
    region: Region.France,
    regionCode: RegionCode.France,
    language: Language.english,
    default: "fr-FR",
    languageCode: LanguageCode.english,
  },
  "fr-FR": {
    path: "fr-FR",
    region: Region.France,
    regionCode: RegionCode.France,
    language: Language.french,
    languageCode: LanguageCode.french,
  },
  "en-UK": {
    path: "en-UK",
    region: Region.UK,
    regionCode: RegionCode.UK,
    language: Language.english,
    languageCode: LanguageCode.english,
  },
  "fr-UK": {
    path: "fr-UK",
    region: Region.UK,
    regionCode: RegionCode.UK,
    language: Language.french,
    languageCode: LanguageCode.french,
    default: "en-UK",
  },
  "cy-UK": {
    path: "cy-UK" as Locale,
    region: Region.UK,
    regionCode: RegionCode.UK,
    language: Language.welsh,
    languageCode: LanguageCode.welsh,
    default: "en-UK" as Locale,
  },
  "ga-UK": {
    path: "ga-UK" as Locale,
    region: Region.UK,
    regionCode: RegionCode.UK,
    language: Language.irish,
    languageCode: LanguageCode.irish,
    default: "en-UK" as Locale,
  },
  "gd-UK": {
    path: "gd-UK" as Locale,
    region: Region.UK,
    regionCode: RegionCode.UK,
    language: Language.scottishGaelic,
    languageCode: LanguageCode.scottishGaelic,
    default: "en-UK" as Locale,
  },
  "en-AU": {
    path: "en-AU",
    region: Region.Australia,
    regionCode: RegionCode.Australia,
    language: Language.english,
    languageCode: LanguageCode.english,
  },
  "en-ALL": {
    path: "en-ALL",
    region: Region.All,
    regionCode: RegionCode.All,
    language: Language.english,
    languageCode: LanguageCode.english,
  },
  "fr-ALL": {
    path: "fr-ALL",
    region: Region.All,
    regionCode: RegionCode.All,
    language: Language.french,
    default: "en-ALL",
    languageCode: LanguageCode.french,
  },
  "es-ALL": {
    path: "es-ALL",
    region: Region.All,
    regionCode: RegionCode.All,
    language: Language.spanish,
    default: "en-ALL",
    languageCode: LanguageCode.spanish,
  },
  "pt-BR": {
    path: "pt-BR" as Locale,
    region: Region.Brazil,
    regionCode: RegionCode.Brazil,
    language: Language.portugueseBR,
    languageCode: LanguageCode.portugueseBR,
  },
  "en-BR": {
    path: "en-BR" as Locale,
    region: Region.Brazil,
    regionCode: RegionCode.Brazil,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "pt-BR" as Locale,
  },
  "pt-PT": {
    path: "pt-PT" as Locale,
    region: Region.Portugal,
    regionCode: RegionCode.Portugal,
    language: Language.portuguesePT,
    languageCode: LanguageCode.portuguesePT,
  },
  "en-PT": {
    path: "en-PT" as Locale,
    region: Region.Portugal,
    regionCode: RegionCode.Portugal,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "pt-PT" as Locale,
  },
  // --- Germany ---
  "de-DE": {
    path: "de-DE" as Locale,
    region: Region.Germany,
    regionCode: RegionCode.Germany,
    language: Language.german,
    languageCode: LanguageCode.german,
  },
  "en-DE": {
    path: "en-DE" as Locale,
    region: Region.Germany,
    regionCode: RegionCode.Germany,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "de-DE" as Locale,
  },
  // --- Spain ---
  "es-ES": {
    path: "es-ES" as Locale,
    region: Region.Spain,
    regionCode: RegionCode.Spain,
    language: Language.spanish,
    languageCode: LanguageCode.spanish,
  },
  "en-ES": {
    path: "en-ES" as Locale,
    region: Region.Spain,
    regionCode: RegionCode.Spain,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "es-ES" as Locale,
  },
  "ca-ES": {
    path: "ca-ES" as Locale,
    region: Region.Spain,
    regionCode: RegionCode.Spain,
    language: Language.catalan,
    languageCode: LanguageCode.catalan,
    default: "es-ES" as Locale,
  },
  "gl-ES": {
    path: "gl-ES" as Locale,
    region: Region.Spain,
    regionCode: RegionCode.Spain,
    language: Language.galician,
    languageCode: LanguageCode.galician,
    default: "es-ES" as Locale,
  },
  "eu-ES": {
    path: "eu-ES" as Locale,
    region: Region.Spain,
    regionCode: RegionCode.Spain,
    language: Language.basque,
    languageCode: LanguageCode.basque,
    default: "es-ES" as Locale,
  },
  // --- Italy ---
  "it-IT": {
    path: "it-IT" as Locale,
    region: Region.Italy,
    regionCode: RegionCode.Italy,
    language: Language.italian,
    languageCode: LanguageCode.italian,
  },
  "en-IT": {
    path: "en-IT" as Locale,
    region: Region.Italy,
    regionCode: RegionCode.Italy,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "it-IT" as Locale,
  },
  "de-IT": {
    path: "de-IT" as Locale,
    region: Region.Italy,
    regionCode: RegionCode.Italy,
    language: Language.german,
    languageCode: LanguageCode.german,
    default: "it-IT" as Locale,
  },
  // --- Mexico ---
  "es-MX": {
    path: "es-MX" as Locale,
    region: Region.Mexico,
    regionCode: RegionCode.Mexico,
    language: Language.spanish,
    languageCode: LanguageCode.spanish,
  },
  "en-MX": {
    path: "en-MX" as Locale,
    region: Region.Mexico,
    regionCode: RegionCode.Mexico,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "es-MX" as Locale,
  },
  // --- Switzerland ---
  "de-CH": {
    path: "de-CH" as Locale,
    region: Region.Switzerland,
    regionCode: RegionCode.Switzerland,
    language: Language.german,
    languageCode: LanguageCode.german,
  },
  "fr-CH": {
    path: "fr-CH" as Locale,
    region: Region.Switzerland,
    regionCode: RegionCode.Switzerland,
    language: Language.french,
    languageCode: LanguageCode.french,
    default: "de-CH" as Locale,
  },
  "it-CH": {
    path: "it-CH" as Locale,
    region: Region.Switzerland,
    regionCode: RegionCode.Switzerland,
    language: Language.italian,
    languageCode: LanguageCode.italian,
    default: "de-CH" as Locale,
  },
  "en-CH": {
    path: "en-CH" as Locale,
    region: Region.Switzerland,
    regionCode: RegionCode.Switzerland,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "de-CH" as Locale,
  },
  "rm-CH": {
    path: "rm-CH" as Locale,
    region: Region.Switzerland,
    regionCode: RegionCode.Switzerland,
    language: Language.romansh,
    languageCode: LanguageCode.romansh,
    default: "de-CH" as Locale,
  },
  // --- Austria ---
  "de-AT": {
    path: "de-AT" as Locale,
    region: Region.Austria,
    regionCode: RegionCode.Austria,
    language: Language.german,
    languageCode: LanguageCode.german,
  },
  "en-AT": {
    path: "en-AT" as Locale,
    region: Region.Austria,
    regionCode: RegionCode.Austria,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "de-AT" as Locale,
  },
  // --- Belgium ---
  "fr-BE": {
    path: "fr-BE" as Locale,
    region: Region.Belgium,
    regionCode: RegionCode.Belgium,
    language: Language.french,
    languageCode: LanguageCode.french,
  },
  "en-BE": {
    path: "en-BE" as Locale,
    region: Region.Belgium,
    regionCode: RegionCode.Belgium,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "fr-BE" as Locale,
  },
  "nl-BE": {
    path: "nl-BE" as Locale,
    region: Region.Belgium,
    regionCode: RegionCode.Belgium,
    language: Language.dutch,
    languageCode: LanguageCode.dutch,
    default: "fr-BE" as Locale,
  },
  "de-BE": {
    path: "de-BE" as Locale,
    region: Region.Belgium,
    regionCode: RegionCode.Belgium,
    language: Language.german,
    languageCode: LanguageCode.german,
    default: "fr-BE" as Locale,
  },
  // --- Netherlands — Dutch (official); Frisian omitted for storefront scope ---
  "nl-NL": {
    path: "nl-NL" as Locale,
    region: Region.Netherlands,
    regionCode: RegionCode.Netherlands,
    language: Language.dutch,
    languageCode: LanguageCode.dutch,
  },
  "en-NL": {
    path: "en-NL" as Locale,
    region: Region.Netherlands,
    regionCode: RegionCode.Netherlands,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "nl-NL" as Locale,
  },
  // --- Poland ---
  "pl-PL": {
    path: "pl-PL" as Locale,
    region: Region.Poland,
    regionCode: RegionCode.Poland,
    language: Language.polish,
    languageCode: LanguageCode.polish,
  },
  "en-PL": {
    path: "en-PL" as Locale,
    region: Region.Poland,
    regionCode: RegionCode.Poland,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "pl-PL" as Locale,
  },
  // --- Sweden ---
  "sv-SE": {
    path: "sv-SE" as Locale,
    region: Region.Sweden,
    regionCode: RegionCode.Sweden,
    language: Language.swedish,
    languageCode: LanguageCode.swedish,
  },
  "en-SE": {
    path: "en-SE" as Locale,
    region: Region.Sweden,
    regionCode: RegionCode.Sweden,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "sv-SE" as Locale,
  },
  // --- Ireland — Irish (Gaeilge) co-official with English ---
  "en-IE": {
    path: "en-IE" as Locale,
    region: Region.Ireland,
    regionCode: RegionCode.Ireland,
    language: Language.english,
    languageCode: LanguageCode.english,
  },
  "ga-IE": {
    path: "ga-IE" as Locale,
    region: Region.Ireland,
    regionCode: RegionCode.Ireland,
    language: Language.irish,
    languageCode: LanguageCode.irish,
    default: "en-IE" as Locale,
  },
  // --- New Zealand — Māori (te reo) official alongside English ---
  "en-NZ": {
    path: "en-NZ" as Locale,
    region: Region.NewZealand,
    regionCode: RegionCode.NewZealand,
    language: Language.english,
    languageCode: LanguageCode.english,
  },
  "mi-NZ": {
    path: "mi-NZ" as Locale,
    region: Region.NewZealand,
    regionCode: RegionCode.NewZealand,
    language: Language.maori,
    languageCode: LanguageCode.maori,
    default: "en-NZ" as Locale,
  },
  // --- India — English + Hindi union-wide; major scheduled languages for coverage ---
  "en-IN": {
    path: "en-IN" as Locale,
    region: Region.India,
    regionCode: RegionCode.India,
    language: Language.english,
    languageCode: LanguageCode.english,
  },
  "hi-IN": {
    path: "hi-IN" as Locale,
    region: Region.India,
    regionCode: RegionCode.India,
    language: Language.hindi,
    languageCode: LanguageCode.hindi,
    default: "en-IN" as Locale,
  },
  "ta-IN": {
    path: "ta-IN" as Locale,
    region: Region.India,
    regionCode: RegionCode.India,
    language: Language.tamil,
    languageCode: LanguageCode.tamil,
    default: "en-IN" as Locale,
  },
  "te-IN": {
    path: "te-IN" as Locale,
    region: Region.India,
    regionCode: RegionCode.India,
    language: Language.telugu,
    languageCode: LanguageCode.telugu,
    default: "en-IN" as Locale,
  },
  "bn-IN": {
    path: "bn-IN" as Locale,
    region: Region.India,
    regionCode: RegionCode.India,
    language: Language.bengali,
    languageCode: LanguageCode.bengali,
    default: "en-IN" as Locale,
  },
  "mr-IN": {
    path: "mr-IN" as Locale,
    region: Region.India,
    regionCode: RegionCode.India,
    language: Language.marathi,
    languageCode: LanguageCode.marathi,
    default: "en-IN" as Locale,
  },
  // --- Argentina ---
  "es-AR": {
    path: "es-AR" as Locale,
    region: Region.Argentina,
    regionCode: RegionCode.Argentina,
    language: Language.spanish,
    languageCode: LanguageCode.spanish,
  },
  "en-AR": {
    path: "en-AR" as Locale,
    region: Region.Argentina,
    regionCode: RegionCode.Argentina,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "es-AR" as Locale,
  },
  // --- Colombia ---
  "es-CO": {
    path: "es-CO" as Locale,
    region: Region.Colombia,
    regionCode: RegionCode.Colombia,
    language: Language.spanish,
    languageCode: LanguageCode.spanish,
  },
  "en-CO": {
    path: "en-CO" as Locale,
    region: Region.Colombia,
    regionCode: RegionCode.Colombia,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "es-CO" as Locale,
  },
  // --- Chile ---
  "es-CL": {
    path: "es-CL" as Locale,
    region: Region.Chile,
    regionCode: RegionCode.Chile,
    language: Language.spanish,
    languageCode: LanguageCode.spanish,
  },
  "en-CL": {
    path: "en-CL" as Locale,
    region: Region.Chile,
    regionCode: RegionCode.Chile,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "es-CL" as Locale,
  },
  // --- Japan ---
  "ja-JP": {
    path: "ja-JP" as Locale,
    region: Region.Japan,
    regionCode: RegionCode.Japan,
    language: Language.japanese,
    languageCode: LanguageCode.japanese,
  },
  "en-JP": {
    path: "en-JP" as Locale,
    region: Region.Japan,
    regionCode: RegionCode.Japan,
    language: Language.english,
    languageCode: LanguageCode.english,
    default: "ja-JP" as Locale,
  },
};

// 2. Define your supportedLocales by referencing these countryLanguages:
const supportedLocalesProd: Partial<Record<Locale, LocaleDetails[]>> = {
  "en-CA": [countryLanguages["en-CA"], countryLanguages["fr-CA"]].filter(
    Boolean
  ) as LocaleDetails[],
  "fr-CA": [countryLanguages["en-CA"], countryLanguages["fr-CA"]].filter(
    Boolean
  ) as LocaleDetails[],
  "en-US": [
    countryLanguages["en-US"],
    countryLanguages["es-US"],
    countryLanguages["fr-US"],
  ].filter(Boolean) as LocaleDetails[],
  "es-US": [
    countryLanguages["en-US"],
    countryLanguages["es-US"],
    countryLanguages["fr-US"],
  ].filter(Boolean) as LocaleDetails[],
  "fr-US": [
    countryLanguages["en-US"],
    countryLanguages["es-US"],
    countryLanguages["fr-US"],
  ].filter(Boolean) as LocaleDetails[],
  "en-FR": [countryLanguages["fr-FR"], countryLanguages["en-FR"]].filter(
    Boolean
  ) as LocaleDetails[],
  "fr-FR": [countryLanguages["fr-FR"], countryLanguages["en-FR"]].filter(
    Boolean
  ) as LocaleDetails[],
  // UK mints only real publishable locales. cy/ga/gd keys stay resolvable for
  // legacy junk pages, but their sibling lists can no longer mint more Welsh/
  // Irish/Gaelic pages (locale-bleed defect: thin auto-translated siblings).
  "en-UK": [countryLanguages["en-UK"], countryLanguages["fr-UK"]].filter(
    Boolean
  ) as LocaleDetails[],
  "fr-UK": [countryLanguages["en-UK"], countryLanguages["fr-UK"]].filter(
    Boolean
  ) as LocaleDetails[],
  "cy-UK": [countryLanguages["en-UK"], countryLanguages["fr-UK"]].filter(
    Boolean
  ) as LocaleDetails[],
  "ga-UK": [countryLanguages["en-UK"], countryLanguages["fr-UK"]].filter(
    Boolean
  ) as LocaleDetails[],
  "gd-UK": [countryLanguages["en-UK"], countryLanguages["fr-UK"]].filter(
    Boolean
  ) as LocaleDetails[],
  // Australia
  "en-AU": [countryLanguages["en-AU"]].filter(Boolean) as LocaleDetails[],
  // Brazil
  "pt-BR": [countryLanguages["pt-BR"], countryLanguages["en-BR"]].filter(Boolean) as LocaleDetails[],
  "en-BR": [countryLanguages["pt-BR"], countryLanguages["en-BR"]].filter(Boolean) as LocaleDetails[],
  // Portugal
  "pt-PT": [countryLanguages["pt-PT"], countryLanguages["en-PT"]].filter(Boolean) as LocaleDetails[],
  "en-PT": [countryLanguages["pt-PT"], countryLanguages["en-PT"]].filter(Boolean) as LocaleDetails[],
  // Germany
  "de-DE": [countryLanguages["de-DE"], countryLanguages["en-DE"]].filter(Boolean) as LocaleDetails[],
  "en-DE": [countryLanguages["de-DE"], countryLanguages["en-DE"]].filter(Boolean) as LocaleDetails[],
  // Spain — co-official regional languages (ca, gl, eu) + Castilian + English
  "es-ES": [countryLanguages["es-ES"], countryLanguages["en-ES"]].filter(Boolean) as LocaleDetails[],
  "en-ES": [countryLanguages["es-ES"], countryLanguages["en-ES"]].filter(Boolean) as LocaleDetails[],
  "ca-ES": [countryLanguages["es-ES"], countryLanguages["en-ES"]].filter(Boolean) as LocaleDetails[],
  "gl-ES": [countryLanguages["es-ES"], countryLanguages["en-ES"]].filter(Boolean) as LocaleDetails[],
  "eu-ES": [countryLanguages["es-ES"], countryLanguages["en-ES"]].filter(Boolean) as LocaleDetails[],
  // Italy — German (Alto Adige / South Tyrol) co-official regionally
  "it-IT": [countryLanguages["it-IT"], countryLanguages["en-IT"]].filter(Boolean) as LocaleDetails[],
  "en-IT": [countryLanguages["it-IT"], countryLanguages["en-IT"]].filter(Boolean) as LocaleDetails[],
  "de-IT": [countryLanguages["it-IT"], countryLanguages["en-IT"]].filter(Boolean) as LocaleDetails[],
  // Mexico
  "es-MX": [countryLanguages["es-MX"], countryLanguages["en-MX"]].filter(Boolean) as LocaleDetails[],
  "en-MX": [countryLanguages["es-MX"], countryLanguages["en-MX"]].filter(Boolean) as LocaleDetails[],
  // Switzerland — national languages + English; Romansh (Grisons)
  "de-CH": [
    countryLanguages["de-CH"],
    countryLanguages["fr-CH"],
    countryLanguages["it-CH"],
    countryLanguages["en-CH"],
  ].filter(Boolean) as LocaleDetails[],
  "fr-CH": [
    countryLanguages["de-CH"],
    countryLanguages["fr-CH"],
    countryLanguages["it-CH"],
    countryLanguages["en-CH"],
  ].filter(Boolean) as LocaleDetails[],
  "it-CH": [
    countryLanguages["de-CH"],
    countryLanguages["fr-CH"],
    countryLanguages["it-CH"],
    countryLanguages["en-CH"],
  ].filter(Boolean) as LocaleDetails[],
  "rm-CH": [
    countryLanguages["de-CH"],
    countryLanguages["fr-CH"],
    countryLanguages["it-CH"],
    countryLanguages["en-CH"],
  ].filter(Boolean) as LocaleDetails[],
  "en-CH": [
    countryLanguages["de-CH"],
    countryLanguages["fr-CH"],
    countryLanguages["it-CH"],
    countryLanguages["en-CH"],
  ].filter(Boolean) as LocaleDetails[],
  // Austria
  "de-AT": [countryLanguages["de-AT"], countryLanguages["en-AT"]].filter(Boolean) as LocaleDetails[],
  "en-AT": [countryLanguages["de-AT"], countryLanguages["en-AT"]].filter(Boolean) as LocaleDetails[],
  // Belgium — Dutch, French, German (official) + English
  "fr-BE": [
    countryLanguages["fr-BE"],
    countryLanguages["nl-BE"],
    countryLanguages["de-BE"],
    countryLanguages["en-BE"],
  ].filter(Boolean) as LocaleDetails[],
  "nl-BE": [
    countryLanguages["fr-BE"],
    countryLanguages["nl-BE"],
    countryLanguages["de-BE"],
    countryLanguages["en-BE"],
  ].filter(Boolean) as LocaleDetails[],
  "de-BE": [
    countryLanguages["fr-BE"],
    countryLanguages["nl-BE"],
    countryLanguages["de-BE"],
    countryLanguages["en-BE"],
  ].filter(Boolean) as LocaleDetails[],
  "en-BE": [
    countryLanguages["fr-BE"],
    countryLanguages["nl-BE"],
    countryLanguages["de-BE"],
    countryLanguages["en-BE"],
  ].filter(Boolean) as LocaleDetails[],
  // Netherlands
  "nl-NL": [countryLanguages["nl-NL"], countryLanguages["en-NL"]].filter(
    Boolean
  ) as LocaleDetails[],
  "en-NL": [countryLanguages["nl-NL"], countryLanguages["en-NL"]].filter(
    Boolean
  ) as LocaleDetails[],
  // Poland
  "pl-PL": [countryLanguages["pl-PL"], countryLanguages["en-PL"]].filter(
    Boolean
  ) as LocaleDetails[],
  "en-PL": [countryLanguages["pl-PL"], countryLanguages["en-PL"]].filter(
    Boolean
  ) as LocaleDetails[],
  // Sweden
  "sv-SE": [countryLanguages["sv-SE"], countryLanguages["en-SE"]].filter(
    Boolean
  ) as LocaleDetails[],
  "en-SE": [countryLanguages["sv-SE"], countryLanguages["en-SE"]].filter(
    Boolean
  ) as LocaleDetails[],
  // Ireland
  "en-IE": [countryLanguages["en-IE"]].filter(
    Boolean
  ) as LocaleDetails[],
  "ga-IE": [countryLanguages["en-IE"]].filter(
    Boolean
  ) as LocaleDetails[],
  // New Zealand
  "en-NZ": [countryLanguages["en-NZ"], countryLanguages["mi-NZ"]].filter(
    Boolean
  ) as LocaleDetails[],
  "mi-NZ": [countryLanguages["en-NZ"], countryLanguages["mi-NZ"]].filter(
    Boolean
  ) as LocaleDetails[],
  // India
  "en-IN": [
    countryLanguages["en-IN"],
    countryLanguages["hi-IN"],
    countryLanguages["ta-IN"],
    countryLanguages["te-IN"],
    countryLanguages["bn-IN"],
    countryLanguages["mr-IN"],
  ].filter(Boolean) as LocaleDetails[],
  "hi-IN": [
    countryLanguages["en-IN"],
    countryLanguages["hi-IN"],
    countryLanguages["ta-IN"],
    countryLanguages["te-IN"],
    countryLanguages["bn-IN"],
    countryLanguages["mr-IN"],
  ].filter(Boolean) as LocaleDetails[],
  "ta-IN": [
    countryLanguages["en-IN"],
    countryLanguages["hi-IN"],
    countryLanguages["ta-IN"],
    countryLanguages["te-IN"],
    countryLanguages["bn-IN"],
    countryLanguages["mr-IN"],
  ].filter(Boolean) as LocaleDetails[],
  "te-IN": [
    countryLanguages["en-IN"],
    countryLanguages["hi-IN"],
    countryLanguages["ta-IN"],
    countryLanguages["te-IN"],
    countryLanguages["bn-IN"],
    countryLanguages["mr-IN"],
  ].filter(Boolean) as LocaleDetails[],
  "bn-IN": [
    countryLanguages["en-IN"],
    countryLanguages["hi-IN"],
    countryLanguages["ta-IN"],
    countryLanguages["te-IN"],
    countryLanguages["bn-IN"],
    countryLanguages["mr-IN"],
  ].filter(Boolean) as LocaleDetails[],
  "mr-IN": [
    countryLanguages["en-IN"],
    countryLanguages["hi-IN"],
    countryLanguages["ta-IN"],
    countryLanguages["te-IN"],
    countryLanguages["bn-IN"],
    countryLanguages["mr-IN"],
  ].filter(Boolean) as LocaleDetails[],
  // Argentina
  "es-AR": [countryLanguages["es-AR"], countryLanguages["en-AR"]].filter(Boolean) as LocaleDetails[],
  "en-AR": [countryLanguages["es-AR"], countryLanguages["en-AR"]].filter(Boolean) as LocaleDetails[],
  // Colombia
  "es-CO": [countryLanguages["es-CO"], countryLanguages["en-CO"]].filter(Boolean) as LocaleDetails[],
  "en-CO": [countryLanguages["es-CO"], countryLanguages["en-CO"]].filter(Boolean) as LocaleDetails[],
  // Chile
  "es-CL": [countryLanguages["es-CL"], countryLanguages["en-CL"]].filter(Boolean) as LocaleDetails[],
  "en-CL": [countryLanguages["es-CL"], countryLanguages["en-CL"]].filter(Boolean) as LocaleDetails[],
  // Japan
  "ja-JP": [countryLanguages["ja-JP"], countryLanguages["en-JP"]].filter(Boolean) as LocaleDetails[],
  "en-JP": [countryLanguages["ja-JP"], countryLanguages["en-JP"]].filter(Boolean) as LocaleDetails[],
} as Partial<Record<Locale, LocaleDetails[]>>;

const supportedLocalesDev: Partial<Record<Locale, LocaleDetails[]>> = {
  // Dev includes everything from prod + any experimental locales
  ...supportedLocalesProd,
} as Partial<Record<Locale, LocaleDetails[]>>;

// Determine which version of supportedLocales to export based on DEV_MODE
export const supportedLocales: Partial<Record<Locale, LocaleDetails[]>> =
  process.env.DEV_MODE ? supportedLocalesDev : supportedLocalesProd;

/**
 * Language codes that must NEVER appear in a sibling-minting list.
 * Auto-minting regional-minority-language siblings (Welsh/Irish/Gaelic pages
 * on en-UK, Catalan/Galician/Basque on es-ES, Romansh on CH…) produced
 * thousands of thin machine-translated pages and burned translation budget —
 * the locale-bleed defect. Enforced by frontend/__tests__/locale-minting.test.ts.
 */
export const BANNED_MINT_LANGUAGE_CODES: LanguageType[] = [
  LanguageCode.welsh,
  LanguageCode.irish,
  LanguageCode.scottishGaelic,
  LanguageCode.catalan,
  LanguageCode.galician,
  LanguageCode.basque,
  LanguageCode.romansh,
];

// Domain-based default languages
export const domainDefaults = {
  // Native mobile-app origin (iOS/Android WebView shells). Serves ALL locales
  // on one host so locale switches never cross eTLDs. Nominal default only —
  // the shell writes the real `locale` cookie from the device locale before
  // the first load, and middleware honors the cookie.
  // ⚠ ORDER MATTERS: getDefaultLanguageForDomain matches with ordered
  // Object.keys().find() + hostname.includes(), and
  // "app.inceptionia.com".includes("inceptionia.com") is true — this entry
  // MUST stay above the "inceptionia.com" entries or it silently inherits US.
  "app.inceptionia.com": {
    path: "en-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "inceptionai.ca": {
    path: "en-CA",
    region: Region.Canada,
    regionCode: RegionCode.Canada,
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "www.inceptionai.ca": {
    path: "en-CA",
    region: Region.Canada,
    regionCode: RegionCode.Canada,
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  // US — canonical apex + www
  "inceptionia.com": {
    path: "en-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "www.inceptionia.com": {
    path: "en-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  // US — legacy hosts (kept for inbound links; canonical is inceptionia.com)
  "inceptionai.shop": {
    path: "en-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "www.inceptionai.shop": {
    path: "en-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "inceptionaiapp.com": {
    path: "en-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "www.inceptionaiapp.com": {
    path: "en-US",
    region: Region["USA"],
    regionCode: RegionCode["USA"],
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "inceptionai.fr": {
    path: "fr-FR",
    region: Region["France"],
    regionCode: RegionCode["France"],
    language: Language.french,
    languageCode: LanguageCode.french,
  } as LocaleDetails,
  "www.inceptionai.fr": {
    path: "fr-FR",
    region: Region["France"],
    regionCode: RegionCode["France"],
    language: Language.french,
    languageCode: LanguageCode.french,
  } as LocaleDetails,
  "inceptionai.uk": {
    path: "en-UK",
    region: Region.UK,
    regionCode: RegionCode.UK,
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "www.inceptionai.uk": {
    path: "en-UK",
    region: Region.UK,
    regionCode: RegionCode.UK,
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "inceptionai.com.au": {
    path: "en-AU",
    region: Region.Australia,
    regionCode: RegionCode.Australia,
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
  "www.inceptionai.com.au": {
    path: "en-AU",
    region: Region.Australia,
    regionCode: RegionCode.Australia,
    language: Language.english,
    languageCode: LanguageCode.english,
  } as LocaleDetails,
};

// 3. Default language (fallback)
export const defaultLanguage: LocaleDetails = domainDefaults["inceptionai.ca"];

// Utility function to get default language based on domain
export function getDefaultLanguageForDomain(hostname: string): LocaleDetails {
  // Check if hostname matches any of our domain defaults
  const domain = Object.keys(domainDefaults).find((d) => hostname.includes(d));
  return domain
    ? domainDefaults[domain as keyof typeof domainDefaults]
    : defaultLanguage;
}

// Utility function to get user's detected region vs current region
export function getRegionMismatch(
  currentLocale: Locale,
  detectedCountryCode: RegionCode
): Locale | null {
  const currentRegion = countryLanguages[currentLocale]?.regionCode;
  const currentLanguage = countryLanguages[currentLocale]?.languageCode;

  if (currentRegion === detectedCountryCode) {
    return null; // No mismatch
  }

  // Find the locale for the detected region with the same language
  // BUT only if it exists in supportedLocales (i.e., actually deployed/available)
  const suggestedLocale = Object.entries(countryLanguages).find(
    ([localePath, details]) =>
      details.regionCode === detectedCountryCode &&
      details.languageCode === currentLanguage &&
      // Check if this locale is actually supported/deployed
      supportedLocales[localePath as Locale] !== undefined
  );

  return suggestedLocale ? (suggestedLocale[0] as Locale) : null;
}

// Utility function to get region-specific default href based on locale
export function getRegionSpecificDefaultHref(
  locale: LocaleDetails
): Record<string, string> {
  const currentRegionSupportedLocales = supportedLocales[locale.path];

  if (currentRegionSupportedLocales) {
    // Generate hrefs for all supported locales in the current region
    return currentRegionSupportedLocales.reduce(
      (acc, localeDetails) => {
        acc[localeDetails.path] = `/${localeDetails.path}`;
        return acc;
      },
      {} as Record<string, string>
    );
  }

  // Fallback: return default hrefs for the current locale's region based on countryLanguages
  const regionLocales = Object.values(countryLanguages).filter(
    (localeDetails) =>
      localeDetails && localeDetails.regionCode === locale.regionCode
  );

  return regionLocales.reduce(
    (acc, localeDetails) => {
      if (localeDetails) {
        acc[localeDetails.path] = `/${localeDetails.path}`;
      }
      return acc;
    },
    {} as Record<string, string>
  );
}

// Utility function to get all available locales from countryLanguages
export function getAllAvailableLocales(): LocaleDetails[] {
  return Object.values(countryLanguages).filter(Boolean) as LocaleDetails[];
}

export const possibleLocales: Locale[] = Object.keys(
  supportedLocales
) as Locale[];

export const LocaleValueSchema = z.lazy(() => {
  const dynamicSchema: {
    [key: string]: z.ZodType<string, z.ZodTypeDef, string>;
  } = {};
  for (const locale of possibleLocales) {
    dynamicSchema[locale] = z.string();
  }
  return z.object(dynamicSchema).partial();
});

export type LocaleValue = z.infer<typeof LocaleValueSchema>;

// Region utility functions that depend on countryLanguages
// Utility function to get available regions (excludes "All" for map/region selection)
export function getAvailableRegions(): Region[] {
  // Dynamically extract unique regions from supportedLocales (actually deployed regions), excluding "All"
  const availableRegions = new Set<Region>();

  Object.keys(supportedLocales).forEach((localePath) => {
    const localeDetails = countryLanguages[localePath as Locale];
    if (localeDetails && localeDetails.region !== Region.All) {
      availableRegions.add(localeDetails.region);
    }
  });

  return Array.from(availableRegions);
}

// Utility function to get available region codes (excludes "All" for map/region selection)
export function getAvailableRegionCodes(): RegionCode[] {
  // Dynamically extract unique region codes from supportedLocales (actually deployed regions), excluding "All"
  const availableRegionCodes = new Set<RegionCode>();

  Object.keys(supportedLocales).forEach((localePath) => {
    const localeDetails = countryLanguages[localePath as Locale];
    if (localeDetails && localeDetails.regionCode !== RegionCode.All) {
      availableRegionCodes.add(localeDetails.regionCode);
    }
  });

  return Array.from(availableRegionCodes);
}

// Utility function to check if a region is available/supported (excludes "All" for map/region selection)
export function isRegionAvailable(regionName: string): boolean {
  const availableRegions = getAvailableRegions();
  return availableRegions.includes(regionName as Region);
}

// Utility function to get default locale for a specific region (excludes "All" for map/region selection)
export function getDefaultLocaleForRegion(regionName: string): Locale | null {
  const region = regionName as Region;
  if (region === Region.All) return null; // Exclude "All" from region selection

  // Dynamically find the default locale for the region from supportedLocales (only deployed regions)
  // Look for a locale without a default property (primary locale) or fallback to first found
  const regionLocales = Object.keys(supportedLocales)
    .map((localePath) => countryLanguages[localePath as Locale])
    .filter((localeDetails) => localeDetails && localeDetails.region === region);

  if (regionLocales.length === 0) return null;

  // Find the primary locale (one without a default property) or use the first one
  const primaryLocale =
    regionLocales.find((locale) => locale && !locale.default) ||
    regionLocales[0];

  return primaryLocale ? primaryLocale.path : null;
}

// Utility function to check if a product is local based on company country and user region
export function isProductLocal(
  companyCountry: string,
  userRegion: Region
): boolean {
  // Import at runtime to avoid circular dependency
  const { getMapRegionName } = require("./region.type");

  // Use the existing getMapRegionName function to normalize the company country
  const normalizedCompanyRegion = getMapRegionName(companyCountry);

  if (!normalizedCompanyRegion) {
    return false;
  }

  // Compare the normalized company region with the user's region
  return normalizedCompanyRegion === userRegion;
}
